import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import type { CellRange, Sheet } from '../core/types'
import { createEvaluator } from '../core/engine'
import { usedRange, useStore } from '../core/store'
import { colName, normalizeRange, ref } from '../core/a1'
import { colW, pageMetrics, rangeHeightPx, rangeWidthPx, rowH, zoomFor } from '../core/pdf'
import type { PdfOptions } from '../core/pdf'
import { mergeAt } from '../core/merges'

function borderCss(border?: { style: string; color: string }): string | undefined {
  if (!border) return undefined
  const width = border.style === 'medium' ? 2 : border.style === 'thick' || border.style === 'double' ? 3 : 1
  const line = ['dashed', 'dotted', 'double'].includes(border.style) ? border.style : 'solid'
  return `${width}px ${line} ${border.color}`
}

export interface PrintJob {
  options: PdfOptions
  /** scope === 'selection' 일 때의 범위 */
  selection?: CellRange
  selectionSheetId?: string
}

/**
 * 인쇄 전용 DOM. 화면에서는 숨겨지고 @media print 에서만 나타난다.
 * 브라우저 인쇄 엔진이 페이지를 나누므로 한글·수식·서식이 벡터로 남는다.
 */
export default function PrintView({ job }: { job: PrintJob }) {
  const wb = useStore((s) => s.wb)
  const version = useStore((s) => s.version)
  const o = job.options

  const targets = useMemo(() => {
    const list: Array<{ sheet: Sheet; range: CellRange }> = []
    const active = wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]

    if (o.scope === 'selection' && job.selection) {
      const s = wb.sheets.find((x) => x.id === job.selectionSheetId) ?? active
      list.push({ sheet: s, range: normalizeRange(job.selection) })
    } else if (o.scope === 'workbook') {
      for (const s of wb.sheets) {
        const u = usedRange(s)
        if (u) list.push({ sheet: s, range: u })
      }
    } else {
      const u = usedRange(active)
      if (u) list.push({ sheet: active, range: u })
    }
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wb, version, o.scope, job.selection, job.selectionSheetId])

  const metrics = pageMetrics(o)

  // .app 은 인쇄 시 숨기므로, 인쇄 DOM 은 body 직속이어야 한다
  return createPortal(
    <div className="print-root">
      {targets.length === 0 && <p className="print-empty">인쇄할 내용이 없습니다.</p>}
      {targets.map(({ sheet, range }, i) => (
        <SheetBlock
          key={sheet.id}
          sheet={sheet}
          range={range}
          options={o}
          metrics={metrics}
          allSheets={wb.sheets}
          first={i === 0}
        />
      ))}
    </div>,
    document.body
  )
}

function SheetBlock({
  sheet,
  range,
  options: o,
  metrics,
  allSheets,
  first,
}: {
  sheet: Sheet
  range: CellRange
  options: PdfOptions
  metrics: ReturnType<typeof pageMetrics>
  allSheets: Sheet[]
  first: boolean
}) {
  const ev = useMemo(() => createEvaluator(allSheets, sheet.name), [allSheets, sheet.name])

  const tableW = rangeWidthPx(sheet, range, o.rowColHeaders)
  const tableH = rangeHeightPx(sheet, range, o.rowColHeaders)
  const zoom = zoomFor(o, metrics, tableW, tableH)

  const cols: number[] = []
  for (let c = range.c1; c <= range.c2; c++) cols.push(c)

  const headRows: number[] = []
  const bodyRows: number[] = []
  const repeat = Math.max(0, Math.min(o.repeatRows, range.r2 - range.r1 + 1))
  for (let r = range.r1; r <= range.r2; r++) (r < range.r1 + repeat ? headRows : bodyRows).push(r)

  const renderRow = (r: number, isHead: boolean) => (
    <tr key={r} style={{ height: rowH(sheet, r) }}>
      {o.rowColHeaders && <th className="pv-rowhdr">{r}</th>}
      {cols.map((c) => {
        const merge = mergeAt(sheet, r, c)
        if (merge && (merge.r1 !== r || merge.c1 !== c)) return null
        const cell = sheet.cells[ref(r, c)]
        const out = ev.display(sheet.name, r, c)
        const s = cell?.s
        const isNum = typeof out.value === 'number' && !s?.align
        return (
          <td
            key={c}
            rowSpan={merge ? merge.r2 - merge.r1 + 1 : undefined}
            colSpan={merge ? merge.c2 - merge.c1 + 1 : undefined}
            className={`pv-cell${isHead ? ' head' : ''}${out.error ? ' err' : ''}`}
            style={{
              fontWeight: s?.bold ? 700 : undefined,
              fontStyle: s?.italic ? 'italic' : undefined,
              textDecoration: s?.underline ? 'underline' : undefined,
              color: s?.color,
              background: s?.bg,
              fontSize: s?.fontSize,
              fontFamily: s?.fontFamily,
              verticalAlign: s?.verticalAlign === 'middle' ? 'middle' : s?.verticalAlign,
              whiteSpace: s?.wrap ? 'pre-wrap' : undefined,
              overflowWrap: s?.wrap ? 'anywhere' : undefined,
              paddingLeft: s?.indent ? 5 + s.indent * 12 : undefined,
              borderTop: borderCss(s?.borderTop),
              borderRight: borderCss(s?.borderRight),
              borderBottom: borderCss(s?.borderBottom),
              borderLeft: borderCss(s?.borderLeft),
              textAlign: s?.align ?? (isNum ? 'right' : undefined),
            }}
          >
            <span className="cell-text" style={{ transform: s?.textRotation ? `rotate(${-s.textRotation}deg)` : undefined }}>
              {out.text}
            </span>
          </td>
        )
      })}
    </tr>
  )

  return (
    <section className={`pv-sheet${first ? '' : ' pv-break'}`}>
      {o.showTitle && <h2 className="pv-title">{sheet.name}</h2>}
      <div className="pv-zoom" style={{ zoom }}>
        <table className={`pv-table${o.gridlines ? ' grid' : ''}`}>
          <colgroup>
            {o.rowColHeaders && <col style={{ width: 44 }} />}
            {cols.map((c) => (
              <col key={c} style={{ width: colW(sheet, c) }} />
            ))}
          </colgroup>
          {(o.rowColHeaders || headRows.length > 0) && (
            <thead>
              {o.rowColHeaders && (
                <tr style={{ height: 22 }}>
                  <th className="pv-corner" />
                  {cols.map((c) => (
                    <th key={c} className="pv-colhdr">
                      {colName(c)}
                    </th>
                  ))}
                </tr>
              )}
              {headRows.map((r) => renderRow(r, true))}
            </thead>
          )}
          <tbody>{bodyRows.map((r) => renderRow(r, false))}</tbody>
        </table>
      </div>
    </section>
  )
}
